import { useState, useEffect } from "react";
import { Save, Loader2, Settings, Globe } from "lucide-react";
import { toast } from "sonner";
import { settingsAPI } from "../../lib/api";
import UserLayout from "../../components/UserLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const AdminSettings = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        ai_provider: "nano_banana",
        tokens_per_generation: 10,
        mayar_webhook_url: ""
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await settingsAPI.get();
            setSettings(response.data);
            setFormData({
                ai_provider: response.data.ai_provider || "nano_banana",
                tokens_per_generation: response.data.tokens_per_generation || 10,
                mayar_webhook_url: response.data.mayar_webhook_url || ""
            });
        } catch (error) {
            toast.error("Gagal memuat settings");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsAPI.update(formData);
            toast.success("Settings berhasil disimpan");
        } catch (error) {
            toast.error("Gagal menyimpan settings");
        } finally {
            setSaving(false);
        }
    };

    const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/payments/webhook`;

    if (loading) {
        return (
            <UserLayout>
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
            </UserLayout>
        );
    }

    return (
        <UserLayout>
            <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-gray-900">Settings</h1>
                    <p className="text-gray-600">Konfigurasi aplikasi</p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                            <Settings className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                            <h2 className="font-heading font-semibold text-gray-900">AI Provider</h2>
                            <p className="text-sm text-gray-500">Pilih provider untuk generate gambar</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>AI Provider</Label>
                            <Select value={formData.ai_provider} onValueChange={(value) => setFormData({ ...formData, ai_provider: value })}>
                                <SelectTrigger data-testid="ai-provider-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="nano_banana">Gemini Nano Banana</SelectItem>
                                    <SelectItem value="bytedance">Bytedance Seedream 4.5</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">
                                {formData.ai_provider === "nano_banana" 
                                    ? "Menggunakan Gemini untuk generate gambar" 
                                    : "Menggunakan Bytedance Seedream 4.5 API"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label>Token per Generate</Label>
                            <Input 
                                type="number" 
                                value={formData.tokens_per_generation} 
                                onChange={(e) => setFormData({ ...formData, tokens_per_generation: parseInt(e.target.value) || 10 })}
                                min={1}
                                data-testid="tokens-per-gen-input"
                            />
                            <p className="text-xs text-gray-500">Jumlah credit yang dipotong per gambar yang digenerate</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                            <Globe className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                            <h2 className="font-heading font-semibold text-gray-900">Mayar Webhook</h2>
                            <p className="text-sm text-gray-500">Konfigurasi callback pembayaran</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Webhook URL (untuk diset di Mayar)</Label>
                            <div className="flex gap-2">
                                <Input 
                                    value={webhookUrl}
                                    readOnly
                                    className="font-mono text-sm bg-gray-50"
                                    data-testid="webhook-url-display"
                                />
                                <Button 
                                    variant="outline" 
                                    onClick={() => {
                                        navigator.clipboard.writeText(webhookUrl);
                                        toast.success("URL disalin ke clipboard");
                                    }}
                                >
                                    Copy
                                </Button>
                            </div>
                            <p className="text-xs text-gray-500">Salin URL ini ke dashboard Mayar untuk menerima notifikasi pembayaran</p>
                        </div>
                    </div>
                </div>

                <Button onClick={handleSave} disabled={saving} className="rounded-xl" data-testid="save-settings-btn">
                    {saving ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Menyimpan...
                        </>
                    ) : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Simpan Settings
                        </>
                    )}
                </Button>
            </div>
        </UserLayout>
    );
};

export default AdminSettings;
